import { format, isValid, parse } from "date-fns";
import { useSelector } from "react-redux";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useParks from "../../../hooks/api-calls/useParks";
import useSpecies from "../../../hooks/api-calls/useSpecies";
import useStopsStatus from "../../../hooks/api-calls/useStopsStatus";
import { RootState } from "../../../redux/store";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import CustomSelect from "../../../components/forms/CustomSelect";
import DateField from "../../../components/forms/DateField";
import { isEqual } from "lodash";
import { PiTrashDuotone } from "react-icons/pi";
import { Button } from 'rizzui';


const toIsoDateString = (value: string | Date): string => {
    if (value instanceof Date) return format(value, 'yyyy-MM-dd');
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = parse(s, 'dd/MM/yyyy', new Date());
    return isValid(d) ? format(d, 'yyyy-MM-dd') : s;
};

const parseFilterDateForPicker = (s: string | undefined): Date | null => {
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T12:00:00`);
    const d = parse(s, 'dd/MM/yyyy', new Date());
    return isValid(d) ? d : null;
};


interface StopsFiltersProps {
    closeFiltersTab: (value: boolean) => void;
    updateFilters: (filters: any) => void;
    resetFilters: (limit: any) => void;
    filters: any;
}


const StopsFilters: React.FC<StopsFiltersProps> = ({ closeFiltersTab, updateFilters, resetFilters, filters }) => {

    // HOOKS

    const { userCan } = usePrivilege();
    const { getParksList } = useParks();
    const { getSpeciesList, getSpeciesListScientific } = useSpecies({ censusAvailable: true });
    const { getStopsStatusList } = useStopsStatus();
    const { user } = useSelector((state: RootState) => state.auth);
    const isSuperAdmin = user?.roles.includes('Superadministrador');
    const { getCompaniesList } = useCompanies();


    // RENDER

    return (
        <>
            <DateField
                isClearable
                className="w-full"
                label="Fecha de Reporte"
                placeholderText="Selecciona las fechas"
                selectedDateField={filters.filter_filters?.between_dates?.type || "createdAt"}
                dateTypesOptions={[
                    { label: "Fecha de parada", value: "stopDate" },
                    { label: "Fecha de creación", value: "createdAt" },
                    { label: "Fecha de modificación", value: "updatedAt" },
                ]}
                startDate={parseFilterDateForPicker(filters.filter_filters?.between_dates?.startDate)}
                endDate={parseFilterDateForPicker(filters.filter_filters?.between_dates?.endDate)}
                onChange={(date: any) => {
                    const filters_between_dates = filters.filter_filters?.between_dates;
                    if (!date) {
                        if (filters_between_dates) updateFilters({ between_dates: null });
                        return;
                    }
                    const next = {
                        startDate: toIsoDateString(date[0]),
                        endDate: toIsoDateString(date[1]),
                        type: date[2] ?? 'createdAt',
                    };
                    if (!isEqual(filters_between_dates, next)) {
                        updateFilters({ between_dates: next });
                    }
                }}
            />
            
            {isSuperAdmin && (
                <CustomSelect
                    isSearchable
                    id={'company'}
                    label="Compañia"
                    options={getCompaniesList()}
                    onChange={(e: any) => { 
                        updateFilters({ company: e.value})
                    }}
                    value={
                        filters.filter_filters?.company
                            ? getCompaniesList().filter((company: any) => filters.filter_filters?.company.includes(company.value))
                            : null
                            
                    }
                    display={userCan('list', 'companies')}
                />
            )}

            <CustomSelect
                isSearchable
                id={'park'}
                label="Parque eólico"
                options={ getParksList() }
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ park: null });
                        return;
                    }
                    updateFilters({ park: [e.value] });
                }}
                value={filters.filter_filters?.park
                    ? getParksList().filter((park: any) => filters.filter_filters?.park.includes(park.value))
                    : null
                }
                display={userCan('admin_park', 'park')}
            />

            <CustomSelect
                isSearchable
                italic
                id={'species'}
                label="Especie Observada"
                options={ getSpeciesListScientific() }
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ species: null });
                        return;
                    }
                    updateFilters({ species: [e.value] });
                }}
                value={filters.filter_filters?.species
                    ? getSpeciesListScientific().filter((species: any) => filters.filter_filters?.species.includes(species.value))
                    : null
                }
                display={userCan('admin_species', 'species')}
            />

            <CustomSelect
                isSearchable
                id={'status'}
                label="Estado de la parada"
                options={ getStopsStatusList() }
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ status: null });
                        return;
                    }
                    updateFilters({ status: [e.value] });
                }}
                value={filters.filter_filters?.status
                    ? getStopsStatusList().filter((status: any) => filters.filter_filters?.status.includes(status.value))
                    : null
                }
            />

            {/* Clear filters button */}
            <Button
                size="sm"
                onClick={() => {
                    resetFilters(50);
                    closeFiltersTab(false);
                }}
                variant="flat"
                className="h-9 bg-gray-200/70"
            >
                <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" /> Limpiar
            </Button>
        </>
    )

}

export default StopsFilters;